import {
	EdgeLabelRenderer,
	getBezierPath,
	type EdgeProps,
} from "@xyflow/react";
import React, { useContext, useMemo, useState } from "react";

import { relationLabel } from "../../types/contextGraph";
import { CardRectsContext } from "./cardRects";
import { edgeVisual } from "./contextGraphTheme";
import { placeEdgeLabel } from "./edgeLabelPlacement";
import type { ContextFlowEdge } from "./layout";

/** Shared arrow marker ids — one <defs> entry each, referenced by every edge. */
export const PATH_MARKER_ID = "context-graph-path-arrow";
export const CONTEXT_MARKER_ID = "context-graph-context-arrow";

/**
 * How far the curve leans out of a card before it turns towards the other end.
 *
 * xyflow's default (0.25 of the span) makes a long edge almost straight and a
 * short one almost a right angle. A fixed lean is what makes two edges between
 * the same pair of columns leave at visibly different angles instead of lying
 * on top of one another.
 */
const CURVATURE = 0.42;

/** Invisible stroke that catches the pointer, so a 1px line is hoverable. */
const HIT_WIDTH = 18;

/**
 * A curved, labelled edge.
 *
 * Every coordinate here is LIVE: xyflow hands this component the current handle
 * positions on every frame, so the curve and its label follow a card the reader
 * drags. The previous version drew a polyline the layout had frozen at mount,
 * which stayed behind when a card moved and — because every edge between the
 * same two columns was given the same gutter — drew half a dozen lines exactly
 * on top of each other.
 *
 * The relation word shows on the edges `layout.ts` picked (the answer's own
 * chain, and any relation rare enough to still be telling the reader something)
 * and on hover for the rest. It is on the path's `<title>` throughout, so it is
 * always reachable.
 */
const ContextGraphEdge: React.FC<EdgeProps<ContextFlowEdge>> = ({
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
	data,
}) => {
	const [hovered, setHovered] = useState(false);
	const cards = useContext(CardRectsContext);

	const [path, labelX, labelY] = getBezierPath({
		sourceX,
		sourceY,
		targetX,
		targetY,
		sourcePosition,
		targetPosition,
		curvature: CURVATURE,
	});

	// A spot on THIS path that touches no card, or `null` for "nowhere clear —
	// say nothing". Recomputed when the path or the cards move, which is what
	// keeps a dragged card's labels correct.
	const site = useMemo(
		() =>
			data
				? placeEdgeLabel(path, relationLabel(data.edge), cards, {
						x: labelX,
						y: labelY,
					})
				: null,
		[data, path, cards, labelX, labelY],
	);

	if (!data) return null;
	const { edge, dimmed, showLabel } = data;
	const visual = edgeVisual(edge);
	const text = relationLabel(edge);
	// A hovered edge lifts out of the wash it was dimmed into: the reader is
	// asking about this line specifically.
	const opacity = dimmed && !hovered ? 0.4 : 1;

	return (
		<>
			{/* Hit area first, so the visible stroke paints over it. */}
			<path
				d={path}
				fill="none"
				stroke="transparent"
				strokeWidth={HIT_WIDTH}
				style={{ pointerEvents: "stroke", cursor: "default" }}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
			>
				<title>{text}</title>
			</path>
			<path
				d={path}
				fill="none"
				className="react-flow__edge-path"
				markerEnd={`url(#${edge.on_path ? PATH_MARKER_ID : CONTEXT_MARKER_ID})`}
				style={{
					stroke: hovered ? "var(--cg-path)" : visual.stroke,
					strokeWidth: hovered ? visual.strokeWidth + 1 : visual.strokeWidth,
					opacity,
					pointerEvents: "none",
					transition: "stroke-width 120ms ease-out, opacity 120ms ease-out",
				}}
			/>
			{site && (showLabel || hovered) && (
				<EdgeLabelRenderer>
					<span
						data-testid={`context-edge-label-${edge.id}`}
						data-on-path={edge.on_path ? "true" : "false"}
						data-hovered={hovered ? "true" : "false"}
						className="context-graph__edge-label"
						style={{
							transform:
								`translate(-50%, -50%) translate(${site.x}px, ${site.y}px)`,
							color: hovered ? "var(--cg-path)" : visual.labelColor,
							opacity,
						}}
					>
						{text}
					</span>
				</EdgeLabelRenderer>
			)}
		</>
	);
};

export default ContextGraphEdge;
